import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { type AiDisclosureContent, AiDisclosureContext } from './AiDisclosureContext';
import { AiDisclosureShim } from './AiDisclosureShim';
import { Environment } from '@/config/environment';

interface State extends AiDisclosureContent {
  isOpen: boolean;
  activeTrigger: HTMLElement | null;
}

const CLOSED: State = {
  isOpen: false,
  text: undefined,
  title: undefined,
  type: undefined,
  activeTrigger: null,
};

// provider is used since we want to have one shared shim across multiple ai labels
export const AiDisclosureProvider: FC<PropsWithChildren & { locale: string }> = ({
  children,
  locale,
}) => {
  const [state, setState] = useState<State>(CLOSED);
  const [showBadge, setShowBadge] = useState(false);

  const open = useCallback((content: AiDisclosureContent, trigger: HTMLElement) => {
    setState({
      isOpen: true,
      text: content.text,
      title: content.title,
      type: content.type,
      activeTrigger: trigger,
    });
  }, []);

  const close = useCallback(() => {
    setState((current) => {
      current.activeTrigger?.focus();
      return { ...current, isOpen: false };
    });
  }, []);

  useEffect(() => {
    const config = Environment.getBuildtimeEnvVars().NEXT_PUBLIC_ENABLE_FEATURE_AI_BADGE;
    if (config.includes(locale)) {
      setShowBadge(true);
    } else {
      setShowBadge(false);
    }
  }, [locale]);

  const value = useMemo(
    () => ({ ...state, open, close, showBadge }),
    [state, open, close, showBadge],
  );

  return (
    <AiDisclosureContext.Provider value={value}>
      {children}
      <AiDisclosureShim />
    </AiDisclosureContext.Provider>
  );
};
